import { supabase } from "@/integrations/supabase/client";
import type { CartItem, ComboCartItem } from "@/hooks/useCart";
import { logger } from "@/lib/logger";

interface ProductIngredientData {
  ingredient_id: string;
  quantity: number;
  ingredient: {
    id: string;
    name: string;
    current_stock: number;
    cost_per_unit: number;
  };
}

interface IngredientDeduction {
  ingredientId: string;
  ingredientName: string;
  totalQuantity: number;
  previousStock: number;
  newStock: number;
}

interface ProductDeduction {
  productId: string;
  productName: string;
  quantity: number;
  previousStock: number;
  newStock: number;
}

/**
 * Deducts stock from ingredients based on the recipes of products in an order.
 * This is called after an order is successfully created.
 * Now supports combo items with proportional deduction for pizza flavors.
 */
export async function deductStockFromOrder(
  restaurantId: string,
  orderId: string,
  items: CartItem[],
  comboItems: ComboCartItem[] = []
): Promise<{ 
  success: boolean; 
  deductions: IngredientDeduction[]; 
  productDeductions: ProductDeduction[];
  errors: string[] 
}> {
  const deductions: IngredientDeduction[] = [];
  const productDeductions: ProductDeduction[] = [];
  const errors: string[] = [];
  
  try {
    // Get the current user for stock movement records
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      logger.error("Stock deduction: User not authenticated");
      return { success: false, deductions: [], productDeductions: [], errors: ["Usuário não autenticado"] };
    }

    // STEP 1: Handle products that track their own stock (tracks_stock = true)
    const productsWithOwnStock = items.filter(item => (item.product as any).tracks_stock === true);
    
    for (const item of productsWithOwnStock) {
      const product = item.product as any;
      const previousStock = Number(product.current_stock) || 0;
      const newStock = Math.max(0, previousStock - item.quantity);

      // Update product stock
      const { error: updateError } = await supabase
        .from("products")
        .update({ current_stock: newStock })
        .eq("id", product.id);

      if (updateError) {
        logger.error(`Error updating stock for product ${product.name}:`, updateError);
        errors.push(`Erro ao atualizar estoque de ${product.name}`);
        continue;
      }

      // Create stock movement record for product
      const unitCost = Number(product.cost_per_unit) || 0;
      const totalCost = item.quantity * unitCost;

      // Insert stock movement record for the product sale
      const { error: movementError } = await supabase
        .from("stock_movements" as any)
        .insert({
          restaurant_id: restaurantId,
          product_id: product.id,
          type: "sale",
          quantity: -item.quantity,
          previous_stock: previousStock,
          new_stock: newStock,
          unit_cost: unitCost,
          total_cost: totalCost,
          order_id: orderId,
          performed_by: user.id,
          reason: `Venda - Pedido`,
        });

      if (movementError) {
        logger.error(`Error creating stock movement for product ${product.name}:`, movementError);
        // Don't fail the whole process, just log
      }

      logger.log(`Product stock deducted: ${product.name} - ${item.quantity} units (${previousStock} -> ${newStock})`);

      productDeductions.push({
        productId: product.id,
        productName: product.name,
        quantity: item.quantity,
        previousStock,
        newStock,
      });
    }

    // STEP 2: Collect all ingredient quantities needed (from regular products and combos)
    const ingredientQuantities = new Map<string, number>();

    // STEP 2a: Handle combo items
    for (const comboItem of comboItems) {
      const { combo, selection, quantity: comboQty } = comboItem;
      
      // For pizza combos: deduct proportionally for each flavor
      if (combo.combo_type === 'pizza' && selection.selectedFlavors && selection.selectedFlavors.length > 0) {
        const flavorCount = selection.selectedFlavors.length;
        const proportionalQty = comboQty / flavorCount;
        
        for (const flavor of selection.selectedFlavors) {
          // Get product ingredients for this flavor
          const { data: flavorIngredients } = await supabase
            .from("product_ingredients" as any)
            .select(`
              ingredient_id,
              quantity,
              ingredient:ingredients(id, name, current_stock)
            `)
            .eq("product_id", flavor.productId);

          if (flavorIngredients) {
            for (const fi of flavorIngredients as any[]) {
              const currentQty = ingredientQuantities.get(fi.ingredient_id) || 0;
              ingredientQuantities.set(
                fi.ingredient_id,
                currentQty + (fi.quantity * proportionalQty)
              );
            }
          }
        }
      }
      
      // For normal combos: deduct all fixed items and selected choices
      if (combo.combo_type === 'normal' && combo.items) {
        for (const item of combo.items) {
          let productIdToDeduct: string | null = null;
          
          if (item.item_type === 'fixed' && item.product_id) {
            productIdToDeduct = item.product_id;
          } else if (item.item_type === 'choice' && selection.selectedChoices) {
            const choice = selection.selectedChoices.find(c => c.comboItemId === item.id);
            if (choice) {
              productIdToDeduct = choice.productId;
            }
          }
          
          if (productIdToDeduct) {
            const { data: itemIngredients } = await supabase
              .from("product_ingredients" as any)
              .select(`
                ingredient_id,
                quantity,
                ingredient:ingredients(id, name, current_stock)
              `)
              .eq("product_id", productIdToDeduct);

            if (itemIngredients) {
              for (const ii of itemIngredients as any[]) {
                const currentQty = ingredientQuantities.get(ii.ingredient_id) || 0;
                ingredientQuantities.set(
                  ii.ingredient_id,
                  currentQty + (ii.quantity * item.quantity * comboQty)
                );
              }
            }
          }
        }
      }
    }

    // STEP 2b: Handle products with recipes (tracks_stock = false or undefined)
    const productsWithRecipes = items.filter(item => !(item.product as any).tracks_stock);
    
    if (productsWithRecipes.length > 0) {
      // Collect all product IDs from items with recipes
      const productIds = productsWithRecipes.map(item => item.product.id);

      // Fetch all product ingredients for these products
      const { data: allProductIngredients, error: allFetchError } = await supabase
        .from("product_ingredients" as any)
        .select(`
          product_id,
          ingredient_id,
          quantity
        `)
        .in("product_id", productIds);

      if (allFetchError) {
        logger.error("Error fetching all product ingredients:", allFetchError);
        return { success: false, deductions: [], productDeductions, errors: ["Erro ao buscar ingredientes"] };
      }

      for (const item of productsWithRecipes) {
        const productRecipe = (allProductIngredients as any[])?.filter(
          (pi: any) => pi.product_id === item.product.id
        );

        if (productRecipe) {
          for (const recipeItem of productRecipe) {
            const currentQty = ingredientQuantities.get(recipeItem.ingredient_id) || 0;
            ingredientQuantities.set(
              recipeItem.ingredient_id,
              currentQty + (recipeItem.quantity * item.quantity)
            );
          }
        }
      }
    }

    // STEP 3: Process ingredient deductions
    if (ingredientQuantities.size === 0) {
      logger.log("Stock deduction: No ingredients to process");
      return { success: true, deductions, productDeductions, errors };
    }

    // Fetch current stock for all affected ingredients
    const ingredientIds = Array.from(ingredientQuantities.keys());
    const { data: ingredients, error: ingredientsError } = await supabase
      .from("ingredients" as any)
      .select("id, name, current_stock, cost_per_unit")
      .in("id", ingredientIds);

    if (ingredientsError) {
      logger.error("Error fetching ingredients:", ingredientsError);
      return { success: false, deductions: [], productDeductions, errors: ["Erro ao buscar estoque"] };
    }

    // Process each ingredient deduction
    for (const ingredient of (ingredients as any[]) || []) {
      const quantityToDeduct = ingredientQuantities.get(ingredient.id) || 0;
      const previousStock = ingredient.current_stock;
      const newStock = Math.max(0, previousStock - quantityToDeduct);

      // Update ingredient stock
      const { error: updateError } = await supabase
        .from("ingredients" as any)
        .update({ current_stock: newStock })
        .eq("id", ingredient.id);

      if (updateError) {
        logger.error(`Error updating stock for ${ingredient.name}:`, updateError);
        errors.push(`Erro ao atualizar estoque de ${ingredient.name}`);
        continue;
      }

      // Create stock movement record with unit cost for CMV calculation
      const unitCost = ingredient.cost_per_unit || 0;
      const totalCost = quantityToDeduct * unitCost;
      
      const { error: movementError } = await supabase
        .from("stock_movements" as any)
        .insert({
          restaurant_id: restaurantId,
          ingredient_id: ingredient.id,
          type: "sale",
          quantity: -quantityToDeduct,
          previous_stock: previousStock,
          new_stock: newStock,
          unit_cost: unitCost,
          total_cost: totalCost,
          order_id: orderId,
          performed_by: user.id,
          reason: `Venda - Pedido`,
        });

      if (movementError) {
        logger.error(`Error creating stock movement for ${ingredient.name}:`, movementError);
        // Don't fail the whole process, just log
      }

      deductions.push({
        ingredientId: ingredient.id,
        ingredientName: ingredient.name,
        totalQuantity: quantityToDeduct,
        previousStock,
        newStock,
      });

      // Log low stock warnings
      if (newStock <= 0) {
        logger.warn(`Stock depleted for: ${ingredient.name}`);
      }
    }

    logger.log(`Stock deduction completed: ${deductions.length} ingredients, ${productDeductions.length} products updated`);
    return { success: true, deductions, productDeductions, errors };

  } catch (error) {
    logger.error("Stock deduction error:", error);
    return { success: false, deductions: [], productDeductions: [], errors: ["Erro interno na baixa de estoque"] };
  }
}
